import React, { useEffect, useState } from 'react'
import { CSVLink } from 'react-csv'
import { buildCsvString } from '../../utils/csv'
import { Button, Select, Tooltip } from 'antd'
import { useDispatch, useSelector } from 'react-redux'
import { fetchVenueInfo } from '../../features/VenueSlice'
import styled from 'styled-components/macro'
import dayjs from 'dayjs'
import {
  buildVenueProductCatalogPriceMap,
  getVenueProductCatalogPrice,
  getVenueReportOrderLineUnitPrice,
} from '../../utils/priceFunctions'
import { VENUES_REPORT_DESCRIPTIONS } from './venuesReportDescriptions'
import ReportGroupSelect from './ReportGroupSelect'
import { canDownloadVenueReportCsv } from './venueReportCsv'

const DownloadAllInfo = ({ venues }) => {
  // headers not needed with string-based CSV export
  const dispatch = useDispatch()
  const venueInfo = useSelector(state => state.venue.venueInfo)
  const [rows, setRows] = useState([])
  const [askingForVenue, setAskingForVenue] = useState(false)
  const [selectedVenueForDownload, setSelectedVenueForDownload] = useState(null)

  useEffect(() => {
    if (venueInfo !== null) {
      const catalogPriceByProductId = buildVenueProductCatalogPriceMap(
        venueInfo.venueProducts,
      )
      const suppliers = venueInfo.venueSuppliers.map(venueSupplier => {
        return { firstCol: venueSupplier.supplierTitle }
      })
      const products = venueInfo.venueProducts.map(venueProduct => {
        return {
          firstCol: venueProduct?.title,
          secondCol: venueProduct?.product?.size,
          thirdCol: venueProduct?.product?.UoM,
          fourthCol: venueProduct?.supplierTitle,
          fifthCol: getVenueProductCatalogPrice(venueProduct),
          sixthCol: venueProduct?.product?.hasVat,
          seventhCol: venueProduct?.product?.sku,
        }
      })
      const orderedProducts = []
      venueInfo.venueOrders.forEach(order => {
        const products = order.products.map(prod => {
          const unitPrice = getVenueReportOrderLineUnitPrice(
            prod?.currentProduct,
            catalogPriceByProductId,
          )
          return {
            firstCol: dayjs(order.orderedDate).format('YYYY-MM-DD HH:MM'),
            secondCol: prod?.currentProduct?.title,
            thirdCol: prod?.currentProduct?.size,
            fourthCol: prod?.currentProduct?.UoM,
            fifthCol: order?.supplierTitles[0],
            sixthCol: unitPrice,
            seventhCol: prod?.currentProduct?.hasVat,
            eighthCol: prod?.currentProduct?.sku,
            ninthCol: prod?.orderedQuantity,
          }
        })
        orderedProducts.push(...products)
      })
      setRows([
        ['Info about:', selectedVenueForDownload?.title],
        [],
        ['Venue:'],
        ['_id:', selectedVenueForDownload?._id],
        ['Title:', selectedVenueForDownload?.title],
        ['City:', selectedVenueForDownload?.addressCity],
        ['First address:', selectedVenueForDownload?.addressLine1],
        ['Second address:', selectedVenueForDownload?.addressLine2],
        ['Phone number:', selectedVenueForDownload?.phoneNumber],
        [],
        [],
        ['Suppliers:'],
        ...suppliers.map(s => [s.firstCol]),
        [],
        [],
        ['Products:'],
        ['Title', 'Size', 'UoM', 'Supplier', 'Price', 'Has_VAT', 'SKU'],
        ...products.map(p => [
          p.firstCol,
          p.secondCol,
          p.thirdCol,
          p.fourthCol,
          p.fifthCol,
          p.sixthCol,
          p.seventhCol,
        ]),
        [],
        [],
        ['Orders for last 30 days'],
        [
          'Ordered date',
          'Title',
          'Size',
          'UoM',
          'Supplier',
          'Price',
          'Has_VAT',
          'SKU',
          'Ordered qty',
        ],
        ...orderedProducts.map(op => [
          op.firstCol,
          op.secondCol,
          op.thirdCol,
          op.fourthCol,
          op.fifthCol,
          op.sixthCol,
          op.seventhCol,
          op.eighthCol,
          op.ninthCol,
        ]),
        [
          'Total ordered:',
          '',
          '',
          '',
          `${venueInfo.venueOrders
            .reduce((sum, order) => {
              const totalByProducts = order.products.reduce(
                (lineSum, product) => {
                  const unitPrice = getVenueReportOrderLineUnitPrice(
                    product.currentProduct,
                    catalogPriceByProductId,
                  )
                  return lineSum + unitPrice * product.orderedQuantity
                },
                0,
              )
              return sum + totalByProducts
            }, 0)
            .toFixed(2)}`,
        ],
      ])
    }
  }, [venueInfo, selectedVenueForDownload])

  const handleOnChange = async value => {
    const selectedVenue = venues.find(venue => venue._id === value)
    setSelectedVenueForDownload(selectedVenue)
    await dispatch(fetchVenueInfo(value))
  }

  const applyVenueIds = ids => {
    const next = ids || []
    if (next.length === 1) {
      handleOnChange(next[0])
      return
    }
    setSelectedVenueForDownload(null)
  }

  const venueCount = selectedVenueForDownload ? 1 : 0
  return (
    <StyledBlock>
      <HelperText data-testid='report-helper'>
        {VENUES_REPORT_DESCRIPTIONS['Venue Report CSV']}
      </HelperText>
      <ReportGroupSelect
        venues={venues}
        onPick={applyVenueIds}
        chooseOne
        onAsking={setAskingForVenue}
      />
      <ControlsRow>
        {askingForVenue ? null : (
          <Select
            showSearch
            allowClear
            placeholder='Select a venue'
            optionFilterProp='children'
            style={{ marginBottom: '15px', width: '355px' }}
            value={selectedVenueForDownload?._id}
            onChange={value => applyVenueIds(value ? [value] : [])}
          >
            {(venues || []).map(venue => (
              <Select.Option key={venue._id} value={venue._id}>
                {venue.title}
              </Select.Option>
            ))}
          </Select>
        )}
        {canDownloadVenueReportCsv(venueCount) &&
        selectedVenueForDownload &&
        venueInfo ? (
          <Button
            className='ant-btn ant-btn-primary'
            style={{ marginRight: '10px' }}
          >
            <CSVLink
              data={buildCsvString(rows)}
              filename={`${selectedVenueForDownload?.title}-All-Info.csv`}
              disabled={!venueInfo || !selectedVenueForDownload}
            >
              Venue report CSV
            </CSVLink>
          </Button>
        ) : (
          <Tooltip title='Firstly select venue'>
            <Button className='ant-btn ant-btn-primary' disabled>
              Venue report CSV
            </Button>
          </Tooltip>
        )}
      </ControlsRow>
    </StyledBlock>
  )
}

const StyledBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const ControlsRow = styled.div`
  display: flex;
  gap: 10px;
`

const HelperText = styled.p`
  font-size: 14px;
  color: #666;
  margin-bottom: 10px;
`

export default DownloadAllInfo
